/**
 * What travels between a player and the server.
 *
 * **The server is the authority.** It owns the world, runs the simulation and
 * decides what happened; a client sends what it is *trying* to do and is told
 * what actually did. The alternative - trusting each client's word about
 * where it is and what it has broken - means the first person to open the
 * console can mine through a wall, stand inside a fortress and conjure a
 * stack of anything. There is no half-way position on this: either the server
 * decides or nobody does.
 *
 * **Two encodings, on purpose.**
 *
 *   - Everything small goes as JSON. It is readable in devtools, it is
 *     trivially versioned, and at a couple of dozen messages a second the
 *     bytes do not matter. Guessing at a binary format for messages that are
 *     not yet the bottleneck is how a protocol becomes unreadable before it
 *     becomes fast.
 *   - Chunks go as binary frames, because a chunk is four thousand and ninety
 *     six Uint16s and JSON would make that a forty-kilobyte string of digits.
 *
 * **Buildings are replayed, not shipped.** A кремль is twenty-one thousand
 * blocks. Sending them would be a megabyte for one right-click, so the server
 * sends "raise this building here, facing this way" and both sides run the
 * same pure function over it. That is only sound because `raise` is
 * deterministic - it takes a building, a facing and a position and nothing
 * else - and it is the single strongest argument for keeping core pure.
 *
 * Pure: types and two pairs of functions. No sockets, no Node, no browser.
 */

/** Bumped when a message changes shape. A mismatch is refused, not guessed. */
import type { Phrase } from "../text/lang.js";
import type { ItemStack } from "../inventory/inventory.js";

export const PROTOCOL_VERSION = 22;

/**
 * Why somebody was turned away.
 *
 * A code beside the sentence, so the client can say the right thing in the
 * player's own language - and so "wrong word" and "wrong version" stay
 * different problems, because they need different things doing about them.
 */
export type Refusal =
  /** Turned away at the door: these end the connection. */
  | "version" | "word" | "world" | "full"
  /**
   * The round started without you.
   *
   * «Король гори» is a race with a minute at the start for everybody to
   * gather. Somebody walking in halfway through it either restarts the race
   * for the people already running it or joins a race they cannot win -
   * both of which happened. The door is open for the gathering minute and
   * shut after it, and this is what it says when it is shut.
   */
  | "late"
  /**
   * That name is somebody else's, or nobody's yet and you gave none.
   *
   * Its own code because it is the one refusal with an obvious answer: pick
   * another name. "Wrong word" and "that name is taken" sent people to
   * completely different fixes and must not look alike.
   */
  | "name"
  /**
   * The building would not go there.
   *
   * Not about the connection at all, which is the whole reason it needs its
   * own code: the client used to read any `no` as the socket failing and put
   * up "зв'язок обірвано" - permanently - because a хата did not fit.
   */
  | "build"
  /**
   * The match is over and this world is being taken down.
   *
   * «Король гори» is one race and «Сучасна війна» is one battle: when it
   * ends there is nothing left to come back to, and the server removes the
   * world a few seconds later. Its own code because the page does something
   * specific with it - says so and goes home - rather than reading it as the
   * connection failing.
   */
  | "closed";

/** How often the server sends the world's moving parts. */
export const SNAPSHOT_HZ = 15;
/** How often a client tells the server where it is trying to be. */
export const INPUT_HZ = 30;

import type { WarPhase } from "../world/war.js";

export interface WorldSeed {
  readonly seed: number;
  readonly sizeXZ: number;
  readonly sizeY: number;
  readonly kremlin: boolean;
  /**
   * What shape of world to grow: islands, or the cube of «Король гори».
   *
   * Travels because the client grows its own terrain from this and the two
   * must agree block for block. Left out means islands, which is what every
   * world was before there was a second shape - so an old save or an old
   * message still means what it always meant.
   */
  readonly terrain?: "islands" | "hill" | "adventure" | "war" | "one";
}

/** Where somebody's drone is, when they have one up. */
export interface DroneState {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

export interface PlayerState {
  readonly id: number;
  readonly name: string;
  readonly skinId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
  /**
   * Their drone, if it is in the air.
   *
   * Carried on the player rather than in a list of its own, because it
   * belongs to exactly one of them and dies with them - a second list would
   * need joining back up to the people at the other end for no gain. Absent
   * when nobody is flying, which is nearly always.
   */
  readonly drone?: DroneState;
  /**
   * What is in their hand, as an item id. Absent when it is empty.
   *
   * The id only. What it *looks* like is the viewer's business - the сабля
   * has two pictures and which one is drawn depends on the 18+ switch of
   * whoever is looking, not of whoever is holding it.
   */
  readonly held?: number;
  /**
   * How many blows they have struck.
   *
   * A count, not a timer. A blow is 0.28 s and snapshots are 15 a second, so
   * a "swinging now" flag would be four samples of a curve and would stick
   * at whatever the last one said between snapshots. A number that goes up
   * says *a blow started*, once, and the body at the other end runs the
   * animation on its own clock from there - which also means a dropped
   * snapshot loses nothing and a doubled one draws nothing twice.
   */
  readonly blows?: number;
}

/** One of the worlds a server is offering, for the picker on the home screen. */
export interface WorldOffer {
  readonly id: string;
  /** Which map it is. Two worlds may be the same map with different seeds. */
  readonly map: string;
  readonly name: Phrase;
  readonly blurb: Phrase;
  readonly players: number;
  /** Whether anybody has opened it yet. A sleeping world is not a closed one. */
  readonly awake: boolean;
  /**
   * Whether a word is wanted at the door.
   *
   * The word itself never travels - this is a padlock on a row in a list, so
   * somebody choosing a world knows whether they will be asked for one
   * before they are asked. A world made without a word is open to anybody.
   */
  readonly locked: boolean;
  /** Who made it, if anybody did. Configured worlds belong to the machine. */
  readonly owner?: string;
  /**
   * This world is on a player's computer, not ours.
   *
   * What it changes for whoever is reading the list: joining it means
   * being introduced to that machine and talking to it directly, and its
   * results never count for anything - the only authority over it is the
   * person hosting it. Left out means ours, which is what every world was
   * before any of them could be anybody else's.
   */
  readonly guest?: true;
}

export interface OrcState {
  readonly id: number;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  /**
   * Showing a blow this instant.
   *
   * Sent only while it is, which is a fifth of a second after each hit and
   * never otherwise - so it costs nothing on the ninety-nine snapshots out of
   * a hundred where nobody has hit anything. Without it a sabre online lands
   * silently and looks like it missed.
   */
  readonly hurt?: boolean;
  /**
   * How many blows it has struck, once it has struck one.
   *
   * A count for the same reason a player's is one: the page draws the swing
   * on its own clock when the number goes up, so a dropped snapshot loses
   * nothing and a repeated one draws nothing twice.
   */
  readonly blows?: number;
}

/**
 * The livestock, the карасі and the villagers, as they travel.
 *
 * The same shape as an orc's state and for the same reason: what a client
 * needs to *draw* one, and nothing else. No hit points - those are the
 * server's - and no wandering state, because a body drawn between two
 * snapshots does not need to know which leg of a stroll it is on.
 *
 * **Sent only to people near enough to see them.** Forty карасі and fourteen
 * pigs in every snapshot to every player is fifteen times a second of animals
 * nobody is looking at. A pig has never been a threat, so there is nothing
 * lost by a player learning about one when they come over the hill.
 */
export interface BeastState {
  readonly id: number;
  readonly kind: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly hurt?: boolean;
}

/**
 * A човен, as where it is and who is in it.
 *
 * The only thing on the wire that is furniture rather than a body: it sits
 * where it was left until somebody gets into it, and then it is wherever
 * they are. `rider` is a client id, so a page can tell its own boat from
 * somebody else's without being told which is which.
 */
export interface TankState {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  /** Shells taken. Five and it is gone. */
  readonly hits: number;
  /** The client id driving it, or null. */
  readonly rider: number | null;
}

export interface BoatState {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly rider: number | null;
}

export interface FishState {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
  readonly hurt?: boolean;
}

export interface VillagerState {
  readonly id: number;
  readonly name: string;
  readonly skinId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  /** What he says when hit, so the voice can be found without asking. */
  readonly line: string;
  /** Counted up, not down: a number that changes means he has just spoken. */
  readonly said?: number;
  readonly hurt?: boolean;
}

/**
 * A player's bag, as it travels.
 *
 * The slots and what is worn, and nothing else - it is the same array the
 * save file holds, so there is one shape for a bag and not two.
 */
export interface BagState {
  readonly slots: readonly (ItemStack | null)[];
  readonly worn: readonly (ItemStack | null)[];
}

/** Which list a blow is aimed at. Ids are per list, not per world. */
/**
 * What a blow can land on.
 *
 * `player` was not on this list, so the ray that picks what you are swinging
 * at never looked at anybody else - it went straight past them to the block
 * behind. Two people on a hill could not touch each other, which on a map
 * whose whole point is being the one left standing at the top is most of the
 * game missing.
 */
export type CreatureKind =
  "orc" | "beast" | "fish" | "villager" | "player";

export interface BlockEdit {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly id: number;
}

/** What a player asks for. None of it is believed without checking. */
export type ClientMessage =
  /**
   * Asking to come in.
   *
   * `word` is what the host hands out to the people they want playing. Not
   * an account and not a login - one shared word for one world, which is the
   * smallest thing that answers "let this person in, and not that one".
   */
  | { readonly t: "hello"; readonly v: number; readonly name: string;
      readonly skinId: string; readonly word: string;
      /**
       * Which world, of the several this server may have.
       *
       * Left out means "I am only knocking": the server answers with the
       * list of worlds and grows none of them. That is what the home screen
       * does before it can offer a choice, and it is why the name travels
       * here rather than in the address - a map is picked in the game, not
       * by typing a different hostname.
       */
      readonly world?: string;
      /**
       * Try the word against that world without going in.
       *
       * The word belongs to the world, so it cannot be checked until one is
       * named - and naming one is what grows it. Without this, finding out
       * you had mistyped the word would mean a кремль built for somebody who
       * is not coming in, and the answer arriving only after the page had
       * reloaded to join.
       */
      readonly look?: boolean;
      /**
       * The secret this world gave us the first time we used this name.
       *
       * There are no passwords here and this is not one. It is a name-tag:
       * the world hands one over when somebody claims a name, the browser
       * keeps it, and showing it again is how you say "that is still me".
       * Without it a name is only a word anybody can type - and a bag is
       * kept against a name, so anybody who knew yours could walk in and
       * take it.
       */
      readonly token?: string;
      /**
       * The password on the account, when the name has one.
       *
       * **Logging in is optional.** A name with no account behind it works
       * the way it always did - claimed on first use and held by a tag the
       * browser keeps. What an account buys is a name that survives a
       * cleared browser, travels to another machine, and cannot be taken by
       * somebody who clears theirs.
       */
      readonly password?: string;
      /**
       * A ticket from our server saying "this is the account called that",
       * for a world on somebody's computer - see `ticket` below. It lets a
       * person back in under their own name after their browser forgot the
       * world's tag (a closed private window does), without the host ever
       * seeing anything that would let them pass as that person elsewhere.
       */
      readonly ticket?: string }
  /**
   * The world I grew, so you can tell me if it is the wrong one.
   *
   * Sent after the client has built its terrain from the seed it was given.
   * Two players in worlds that differ is the worst failure this design can
   * have - everything would look fine and nothing would line up, blocks
   * appearing in mid-air and people walking through hillsides. It costs three
   * numbers to refuse it outright.
   */
  | { readonly t: "ready"; readonly seed: number; readonly sizeXZ: number;
      readonly sizeY: number }
  /**
   * Where the player believes it is.
   *
   * A position rather than a key press: the client predicts its own movement
   * so the game feels immediate, and the server's job is to decide whether
   * that position is one it could have got to. A key press would mean the
   * server replaying every frame, which is a bigger machine for no gain here.
   */
  | { readonly t: "move"; readonly x: number; readonly y: number; readonly z: number;
      readonly yaw: number; readonly pitch: number;
      /**
       * And where their drone is, if they are flying one.
       *
       * On the same message as their body rather than one of its own: the two
       * move together, fifteen times a second, and a drone that arrived on a
       * different beat would jitter against the person flying it.
       */
      readonly drone?: DroneState;
      /**
       * What is in their hand, and how many blows they have struck.
       *
       * On `move` rather than on messages of their own for the same reason
       * the drone is: they change with the body, fifteen times a second, and
       * a hand that arrived on a different beat would be holding last
       * frame's axe. Both are left out when there is nothing to say - an
       * empty hand and a man who has never swung are the common case.
       *
       * Neither is checked, and neither needs to be. Lying about what you
       * are holding draws a picture for other people; lying about how many
       * times you have swung draws an arm moving. What a blow *does* is
       * `strike`, and that is checked.
       */
      readonly held?: number;
      readonly blows?: number;
      /**
       * Whether they are in creative, which is to say off the menu.
       *
       * Sent because the server is the one deciding who the orcs go after,
       * and it cannot see the mode a player put themselves in. Left out
       * while they are walking, which is nearly always.
       */
      readonly fly?: boolean }
  | { readonly t: "place"; readonly x: number; readonly y: number; readonly z: number;
      readonly id: number }
  | { readonly t: "break"; readonly x: number; readonly y: number; readonly z: number }
  /**
   * "I changed this block to this."
   *
   * For everything that is neither placing nor breaking: a door swinging, a
   * stove catching, a чан filling, a bed being made. Every one of those goes
   * through its own `setBlock` in the game, and without this each was
   * happening on one screen only - two people standing at the same door,
   * seeing it open and shut.
   *
   * Not `place` with the checks turned off. `place` refuses to write into an
   * occupied cell, which is exactly what a door does when it opens, so the
   * two are different questions and share only a shape. What they do share is
   * the check that matters: the server believes neither of them beyond arm's
   * length.
   */
  | { readonly t: "set"; readonly x: number; readonly y: number; readonly z: number;
      readonly id: number }
  /**
   * A вогняний снаряд, thrown or dropped.
   *
   * An instruction rather than a thing, the same way a building is: where it
   * left from and which way it went. Everybody runs the same pure flight over
   * it and it lands in the same place, and the server runs it too - so the
   * fire it starts is the server's fire, and what burns away burns away for
   * everybody.
   *
   * That is also what makes it checkable. A bolt's *impact* can be three
   * hundred blocks off and no server could tell an honest one from a lie; a
   * bolt's *launch* is at the thrower's hand or at their drone, and both of
   * those the server already knows.
   */
  | { readonly t: "bolt"; readonly x: number; readonly y: number; readonly z: number;
      readonly dx: number; readonly dy: number; readonly dz: number }
  /**
   * "I hit that orc."
   *
   * The orcs live on the server, so this is the only way one can be hurt. The
   * damage is the client's to name, which is the same trust `place` gets: it
   * is the id of what is in your hand, and a bag is not authoritative yet.
   * What the server does check is that you were near enough to swing at it.
   */
  /**
   * "I am putting a човен on the water there."
   *
   * A column rather than a point: a boat floats at the top of whatever water
   * is in that column, and where exactly that is is the server's to work out
   * - it is the only one of us who can be wrong about it and still be right
   * for everybody. The item is spent on the client, which is the same trust
   * every other placement gets.
   */
  | { readonly t: "launch"; readonly x: number; readonly z: number }
  /** "I am getting into that one." Refused if somebody is already in it. */
  | { readonly t: "board"; readonly id: number }
  /**
   * "That is the way my hull is pointing."
   *
   * Only the heading. Where the boat *is* comes from the rider's own `move`,
   * because while somebody is in a boat the boat is where they are - so a
   * position here would be the same number twice, arriving on two different
   * beats.
   */
  | { readonly t: "row"; readonly yaw: number }
  /** "I have got out of it." Where I got out to arrives with the next move. */
  | { readonly t: "ashore" }
  /** "I have picked that one up." It goes back in the bag, which is mine. */
  | { readonly t: "scuttle"; readonly id: number }
  /*
   * The танк, which travels exactly as the човен does and for the same
   * reasons: where it *is* comes from the driver's own `move`, because
   * while somebody is in it the machine is where they are.
   */
  /** "I am putting a танк down there." */
  | { readonly t: "deploy"; readonly x: number; readonly y: number;
      readonly z: number; readonly yaw: number }
  /** "I am getting into that one." Refused if somebody is already in it. */
  | { readonly t: "mount"; readonly id: number }
  /** "That is the way the hull is pointing." */
  | { readonly t: "steer"; readonly yaw: number }
  /** "I have got out." Where I got out to arrives with the next move. */
  | { readonly t: "dismount" }
  /**
   * "I have fired, from this point, at this speed."
   *
   * The shell is flown on both sides - a gun that waited for a round trip
   * could not be aimed - and what it *hit* is the server's, which is where
   * the five hits are counted.
   */
  | { readonly t: "shell"; readonly x: number; readonly y: number;
      readonly z: number; readonly vx: number; readonly vy: number;
      readonly vz: number; readonly from: number }
  /**
   * "I struck a spark into that block."
   *
   * A кресало, which is the only way to light a fire without a siege engine.
   * Where rather than what: the server looks at the cell itself and refuses
   * anything that will not catch, so the worst a liar can do is point at a
   * haystack they could have reached anyway. Checked for reach the same way
   * a dug block is.
   */
  | { readonly t: "spark"; readonly x: number; readonly y: number;
      readonly z: number }
  | { readonly t: "strike"; readonly kind: CreatureKind; readonly id: number;
      readonly damage: number }
  /**
   * "I loosed an arrow, from here, that way."
   *
   * A launch, not an impact - the same reasoning as a bolt. An arrow that
   * claims to have hit an orc forty blocks off is a claim no server can
   * check; an arrow that claims to have *left somebody's bow* is checked
   * against where that somebody is standing. The server flies it and decides
   * what it hit.
   */
  | { readonly t: "shoot"; readonly x: number; readonly y: number; readonly z: number;
      readonly dx: number; readonly dy: number; readonly dz: number }
  /** Put a whole building down. Replayed by everyone, never shipped block by block. */
  /**
   * "This is what I am carrying."
   *
   * Sent whenever it changes, and kept by the server against the name they
   * came in under, so it is there when they come back.
   *
   * **The server does not decide it yet.** Every act that changes a bag -
   * picking a thing up, crafting, spending a block to place it - still
   * happens on the client, so this is the client's word for what it has.
   * That makes the bag *real*, in that it outlives a browser and belongs to
   * the world rather than to a tab. It does not make it honest, and the
   * difference is written down in NETWORK.md rather than hinted at here.
   */
  | { readonly t: "bag"; readonly bag: BagState }
  /**
   * "Hold this name for me, with this password."
   *
   * Its own message rather than a flag on `hello`, because it is a different
   * act: hello asks to come into a world, this asks the machine to remember
   * somebody. It is answered and the socket closes - the page then joins as
   * it always does.
   */
  | { readonly t: "register"; readonly name: string; readonly password: string;
      /** Optional, and the only way a password can ever be reset. */
      readonly email?: string }
  /**
   * "I have forgotten it - write to me."
   *
   * Answered identically whatever happens: no such name, no address on it,
   * mail refused, mail sent. Anything else tells somebody working through
   * names which of them exist, which is most of what they wanted.
   */
  | { readonly t: "forgot"; readonly name: string }
  /**
   * "Give me a ticket into that world under my name." From a signed-in
   * guest, before knocking on a world held in a browser: answered with a
   * ticket good for that one world, for a few minutes, and nothing else.
   */
  | { readonly t: "ticket"; readonly name: string; readonly token: string;
      readonly world: string }
  /**
   * "Is this ticket what it says?" From a host, about somebody at its door.
   * Answered yes only for a ticket our server made, for that name, into the
   * world this host is holding, and not yet out of date.
   */
  | { readonly t: "vouch"; readonly name: string; readonly ticket: string }
  /**
   * "It is me." A name and its password, answered with the token the page
   * comes back with from then on - so that signing in is its own act on
   * the home screen, done once, and making a world is another.
   */
  | { readonly t: "login"; readonly name: string; readonly password: string }
  /**
   * "Make me a world of this map."
   *
   * An account and its token, because a world is a directory on somebody
   * else's machine and a stranger who opened a socket has not earned one.
   * The word is optional: without one, anybody who can see the list can walk
   * in. Answered with the world's id, and the page then joins it the way it
   * joins any other - so making a world and entering one stay two acts, and
   * the second is the one every check already covers.
   */
  | { readonly t: "create"; readonly map: string; readonly name: string;
      /**
       * Either will do, the same as at the door.
       *
       * A page that has joined once holds a token; one that has only just
       * filled the password in holds a password. Asking for a token alone
       * would mean logging in somewhere else first and then coming back,
       * which is a round trip and a screen for nothing.
       */
      readonly token?: string; readonly password?: string;
      readonly word?: string;
      /**
       * «Сучасна війна» only, and settled when the world is made.
       *
       * "Той хто створює гру вибирає чекбоксом «проти ботів»", and "час на
       * підготовку задає створювач гри". Both are decisions about what
       * match this is, so they travel with the act that creates it rather
       * than being argued about in the lobby.
       */
      readonly bots?: boolean; readonly prep?: number }
  /** A ticket out of that message, and the new password. */
  /**
   * The lobby of «Сучасна війна»: make a team, or join one.
   *
   * Two things in one message because they are one screen and one moment -
   * the lobby is the only place either can be sent, and a message that can
   * only arrive in one state is easier to refuse wrongly when it is split
   * in two. `make` is the owner's ("він нажимає створити команду і вводить
   * назву"); `join` is anybody's.
   */
  | { readonly t: "team"; readonly make?: string; readonly join?: number }
  /** The owner says the teams are gathered: "створювач нажимає почати". */
  | { readonly t: "begin" }
  /**
   * "I have just died, and this is who did it."
   *
   * A player's сила is their own page's business - see `strike` - so the
   * server is the last to know that anybody has died, and it has to be told
   * before it can keep a table of kills and deaths. `by` is the client id of
   * whoever struck the blow that finished it, or 0 for a fall, a fire or an
   * orc.
   */
  | { readonly t: "died"; readonly by?: number }
  | { readonly t: "reset"; readonly ticket: string; readonly password: string }
  | { readonly t: "raise"; readonly building: string; readonly facing: number;
      readonly x: number; readonly y: number; readonly z: number }
  /*
   * Below here: being introduced to somebody else's machine.
   *
   * None of these carries a block or a position. They are how two players
   * find each other, and once they have, the game goes straight between
   * them and this server sees none of it. See `src/core/net/signal.ts`.
   */
  /**
   * "I am holding a world; put it on the list."
   *
   * An account, the same as making one here - a row in the list wears the
   * name of whoever is hosting it, and a name in a list has to mean
   * somebody. `locked` says whether a word will be wanted at that door; the
   * word itself stays on the host's machine, because it is the host who
   * will check it.
   */
  | { readonly t: "host"; readonly map: string; readonly name: string;
      readonly token?: string; readonly password?: string;
      readonly locked?: boolean;
      /**
       * Only asking: would this be taken? Answered the same way, but no
       * world is opened. The home screen asks before it reloads, so a
       * refusal is a line under the button and not a page that came back
       * to where it started with a notice nobody read.
       */
      readonly dry?: boolean }
  /** "I have stopped." Their link going also does this. */
  | { readonly t: "unhost" }
  /** How many are in there. Their count: we cannot see inside their world. */
  | { readonly t: "holding"; readonly world: string; readonly players: number }
  /**
   * "Put me through to that world."
   *
   * `sdp` is a session description - where this browser can be reached and
   * what it can speak. It is passed to the host as-is; this server does not
   * read it, and the day WebRTC changes it should not have to.
   */
  | { readonly t: "offer"; readonly world: string; readonly sdp: string }
  /** The host's half of the same, for one particular person waiting. */
  | { readonly t: "answer"; readonly to: number; readonly sdp: string }
  /**
   * One more way of being reached, found after the offer went out.
   *
   * `to` only from a host, who has several people waiting. A guest is
   * waiting on exactly one world and does not have to say which.
   */
  | { readonly t: "ice"; readonly to?: number; readonly line: string }
  /** "We are through." Nothing else would ever tell us. */
  | { readonly t: "through" };

/** What the server says happened. */
export type ServerMessage =
  | { readonly t: "welcome"; readonly v: number; readonly you: number;
      /**
       * Which world they landed in, so the screen can say where they are.
       *
       * The world's id, not the map's - two worlds may be the same map, and
       * naming this `map` had it reporting "druha" as a map that does not
       * exist. `WorldOffer` carries both, and keeps them apart.
       */
      readonly worldId: string;
      /** Keep this and send it back next time. See `token` on `hello`. */
      readonly token: string;
      readonly world: WorldSeed; readonly tick: number;
      /**
       * What hour of the day this world is at, 0..1.
       *
       * The realm keeps one clock and everybody reads it. Before this each
       * page ran its own: two people in one world stood in the same field at
       * noon and at midnight, and neither of them was wrong. It matters more
       * now than it looked, because what comes out of a lake after dark has
       * to come out of it for everybody.
       */
      readonly time: number }
  /**
   * What this server has, for somebody who knocked without naming a world.
   *
   * Answered after the word is checked and never before: being turned away
   * should tell you that you were turned away and nothing else.
   */
  | { readonly t: "worlds"; readonly list: readonly WorldOffer[] }
  /**
   * "You are actually here."
   *
   * Sent when the server will not believe where somebody says they are -
   * during the gathering minute on a hill, or after a gap long enough that
   * the distance covered is more than anybody could have walked. It used to
   * be sent as a `players` snapshot holding one entry, which was wrong twice
   * over: the page replaces its whole list from a snapshot, so everybody
   * else's body vanished for a frame and came back with the next one -
   * "гравці постійно мерехтять один для одного" - and the page never moved
   * the player at all, because your own body is not drawn from that list.
   *
   * Which left the second fault: once a lag spike put somebody past the
   * distance cap, every move after it was measured from the same stale place
   * and refused too. They went on playing, climbing, and winning on their
   * own screen while standing still on everybody else's, and the hill was
   * judged on where the server still thought they were.
   */
  | { readonly t: "where"; readonly x: number; readonly y: number;
      readonly z: number;
      /**
       * And this is where you come back to from now on.
       *
       * «Сучасна війна» puts every team in its own quarter - "кожна команда
       * з'являється на своїй частині мапи" - and somebody who dies there has
       * to wake up in it, not at whatever the world's own spawn happens to
       * be. Without this the second life of every match was spent walking
       * back from the middle of the map.
       */
      readonly home?: boolean }
  /**
   * The hour, again, every so often.
   *
   * Both ends run the same clock at the same speed, so this is a correction
   * and not a heartbeat: it goes out rarely and the sky moves smoothly
   * between. A page that has been open an hour has drifted by a few seconds
   * of game time, which nobody can see; one that was asleep in a background
   * tab has drifted by an hour, which everybody can.
   */
  | { readonly t: "sky"; readonly time: number }
  /**
   * "That word does open that world."
   *
   * The answer to a `look`. Says nothing about the world itself - not the
   * seed, not the size, not who is in it - because none of that has been
   * asked for yet and the world may not even be grown.
   */
  | { readonly t: "open"; readonly world: string }
  /** Refused, with a code to act on and a sentence to read. */
  | { readonly t: "no"; readonly code: Refusal; readonly why: string }
  | { readonly t: "blocks"; readonly edits: readonly BlockEdit[] }
  | { readonly t: "raised"; readonly building: string; readonly facing: number;
      readonly x: number; readonly y: number; readonly z: number }
  /** Somebody threw one. Flown here as well, so it is seen and not only felt. */
  /**
   * Somebody struck a spark over there.
   *
   * The server has already lit its own fire; this is so every client lights
   * the same cell and the flames are drawn for everybody, the same as a bolt.
   */
  | { readonly t: "spark"; readonly x: number; readonly y: number;
      readonly z: number }
  /**
   * A bolt in the air, and which kind of fire it is.
   *
   * `cold` is Чахлик's: blue, faster, flatter, and it sets nothing alight.
   * Absent means the трeбушет's, which is a stone in burning смола and whose
   * whole purpose is the fire it starts.
   */
  | { readonly t: "bolt"; readonly x: number; readonly y: number; readonly z: number;
      readonly dx: number; readonly dy: number; readonly dz: number;
      readonly cold?: boolean }
  | { readonly t: "players"; readonly list: readonly PlayerState[] }
  | { readonly t: "orcs"; readonly list: readonly OrcState[] }
  /**
   * Everything else that is alive, and near enough to matter.
   *
   * One message rather than three: they arrive together, they are drawn
   * together, and three would be three round trips through the same filter.
   */
  | { readonly t: "herd"; readonly beasts: readonly BeastState[];
      readonly fish: readonly FishState[];
      readonly folk: readonly VillagerState[];
      /**
       * And the човни, which are not alive but arrive and are drawn with
       * everything that is, and are filtered by the same distance.
       */
      readonly boats: readonly BoatState[];
      /** And the танки, on the one map that has any. */
      readonly tanks?: readonly TankState[] }
  /**
   * Something got you.
   *
   * The server decides *that* you were hit; your own body still works out
   * what your armour did about it. Not because that is safer - it is not -
   * but because armour, falling and drowning are all still yours, and a
   * health bar with two owners is a health bar that flickers.
   */
  /**
   * What the шопопало-блок gave you, on «Один блок».
   *
   * The world's own block is the world's to open: the server keeps the count
   * of how many times it has been hit and works out what comes out, so
   * everybody's table stays in step and nobody's page can decide it has just
   * been given a танк. A creature is not in this message at all - the server
   * simply puts one in the world, and it arrives in the next snapshot like
   * everything else alive.
   */
  | { readonly t: "gave"; readonly item: number; readonly count: number }
  | { readonly t: "hurt"; readonly amount: number;
      /** Which player struck it, when one did. See the `died` message. */
      readonly by?: number;
      /**
       * Who did it, when it was not an orc.
       *
       * Absent means the thing that has always done it, which is the raid -
       * so an older page reads a blow from a person as a blow from an орк
       * rather than as nothing at all. The line on the screen is the only
       * thing that changes, and "орк дістав тебе" on a hill with no orcs
       * within a thousand blocks is a line that is simply wrong.
       */
      readonly from?: "player" }
  /**
   * Somebody loosed one - an orc, or another player.
   *
   * Drawn and nothing more. The server flew it and has already said what it
   * hit; a client that worked out its own answer would be a second opinion
   * about a thing that has one owner.
   */
  | { readonly t: "arrow"; readonly x: number; readonly y: number; readonly z: number;
      readonly dx: number; readonly dy: number; readonly dz: number }
  /**
   * An orc went down, and what it was carrying.
   *
   * The roll is the server's. Left to each client, everybody would find
   * different gold on the same body - which is the one thing worse than
   * nobody finding any.
   */
  | { readonly t: "fell"; readonly x: number; readonly y: number; readonly z: number;
      readonly drops: readonly { readonly item: number; readonly count: number }[] }
  /**
   * A вибухоблок went off, here.
   *
   * The blocks it took are already travelling as ordinary edits - this is
   * only so it can be *heard*, and heard from across the hill rather than
   * only by whoever set it off. A soundless explosion that removes forty
   * blocks reads as the world glitching.
   */
  | { readonly t: "boom"; readonly x: number; readonly y: number;
      readonly z: number;
      /**
       * A танк going up rather than a вибухоблок.
       *
       * The two make the same noise and do very different things: a tank
       * takes half of everybody's сила within four blocks and leaves the
       * ground alone, which is its own rule and not the hill's.
       */
      readonly tank?: boolean }
  /**
   * Who is highest, and how long they have been.
   *
   * The whole of «Король гори». Sent only when it changes hands or the count
   * passes a whole second, because it is a line of text and not a position.
   * `id` is nought when the hill is nobody's.
   */
  /**
   * Who is on top, and where the round has got to.
   *
   * `held` is what it was when the crown was a hold rather than a round -
   * kept at nought and unread, because taking a field out of a message is
   * not worth a protocol version on its own. `phase` and `left` are what a
   * screen needs: a minute of gathering in which nobody may move, and then
   * five minutes to climb.
   */
  /**
   * Who is on top, and - when a round ends - that it has.
   *
   * `over` says the round finished, **whether or not anybody won it**, and
   * `name` says who did: empty when nobody was high enough, and empty when
   * two people were level, because `highest` refuses to pick between a tie.
   *
   * It was `won`, and it was sent as `id !== 0` - so a round that ended with
   * nobody on the hill, or with two people at the same height, told the page
   * nothing at all. The page shows its end-of-round screen off this flag, so
   * "nothing at all" meant the round silently began again under whoever was
   * playing, with the mouse still theirs and the two buttons never drawn.
   * Reported twice: "гра почалась спочатку" and "неможливо нажати «грати ще
   * раз» бо мишка продовжує керувати персонажем".
   *
   * The single-player hill never had the bug: it calls the same screen with
   * an empty name and lets it say «Ніхто не дійшов». This is that, over the
   * wire.
   */
  | { readonly t: "king"; readonly id: number; readonly name: string;
      readonly held: number; readonly over: boolean;
      readonly phase?: "gather" | "climb"; readonly left?: number }
  /**
   * What this world was keeping for you.
   *
   * Sent once, on the way in, and only when there is something: a player
   * arriving somewhere new keeps whatever they walked in with.
   */
  | { readonly t: "bag"; readonly bag: BagState }
  /**
   * The answer to `register`, `forgot` or `reset`.
   *
   * One message for all three because the screen does one thing with it:
   * says it. `ok` false carries a key the interface translates rather than a
   * sentence, so the server never decides what language somebody reads.
   */
  /** The world that was just made, ready to be joined by name. */
  | { readonly t: "made"; readonly ok: boolean; readonly world?: string;
      readonly why?: string }
  /*
   * Below here: the introduction, from this end.
   *
   * See the same note on the client's half. Nothing here is about the game;
   * it is about two players finding each other, after which they stop
   * needing us.
   */
  /** The answer to `host`: the name their world now wears in the list. */
  | { readonly t: "hosting"; readonly ok: boolean; readonly world?: string;
      readonly why?: string }
  /**
   * "Somebody wants in." Only ever sent to a host, about their own world.
   *
   * `from` is who to answer, and the only id an answer will be accepted
   * for - so a host cannot be made to answer a person it was never offered.
   */
  | { readonly t: "call"; readonly from: number; readonly sdp: string }
  /** The host's half, coming back to the one person it was meant for. */
  | { readonly t: "answer"; readonly sdp: string }
  /** One more way of reaching whoever sent it. */
  | { readonly t: "ice"; readonly from: number; readonly line: string }
  /**
   * The introduction did not happen, and why.
   *
   * Its own message rather than a `no`, because a `no` is the door of a
   * world and the page treats one as the connection failing. Failing to be
   * introduced to somebody else's machine is neither.
   */
  | { readonly t: "nocall"; readonly why: string }
  /** The answer to `ticket`: one, or none. */
  | { readonly t: "ticket"; readonly ticket?: string }
  /** The answer to `vouch`. */
  | { readonly t: "vouched"; readonly name: string; readonly ok: boolean }
  | { readonly t: "account"; readonly ok: boolean; readonly why?: string;
      /** On a successful register: the tag to come back with. */
      readonly token?: string }
  /**
   * Where a match of «Сучасна війна» has got to.
   *
   * One message for the lobby, the preparing, the fighting and the table at
   * the end, because a page has one screen for all four and the difference
   * between them is `phase`. Sent whenever anything in it changes and once
   * a second while the clock runs.
   *
   * `you` is the team the receiving player is on, or -1 for nobody yet:
   * everything a page draws differently for "us" and "them" hangs off it.
   */
  | { readonly t: "war"; readonly phase: WarPhase; readonly left: number;
      readonly teams: readonly WarTeamState[]; readonly you: number;
      readonly owner: boolean;
      /** Supplies by every flag: every page lays the same скрині. */
      readonly hardcore?: boolean;
      /** Seconds until the world ends. */
      readonly ends?: number;
      /** Over because the time ran out. */
      readonly timeUp?: boolean;
      /** Only at the end: every player, winners first. */
      readonly scores?: readonly WarScoreState[] }
  | { readonly t: "bye"; readonly id: number };

export interface WarTeamState {
  readonly id: number;
  readonly name: string;
  readonly bots: boolean;
  readonly members: readonly string[];
  /** Whether their прапор is still standing. */
  readonly alive: boolean;
}

export interface WarScoreState {
  readonly name: string;
  readonly team: number;
  readonly kills: number;
  readonly deaths: number;
}

export function encode(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}

/**
 * Read a message off the wire.
 *
 * Returns null rather than throwing for anything that is not a message we
 * know: a malformed frame is something a stranger can send at any time, and
 * one of them must not be able to stop the server. The caller decides whether
 * to answer, ignore or hang up.
 */
export function decode(raw: string): ClientMessage | ServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const t = (parsed as { t?: unknown }).t;
  if (typeof t !== "string") return null;
  return parsed as ClientMessage | ServerMessage;
}

/*
 * The chunk frame.
 *
 * [ 0..3 ]  magic, so a stray text frame is not read as a chunk
 * [ 4..5 ]  cx   (signed 16)
 * [ 6..7 ]  cy
 * [ 8..9 ]  cz
 * [ 10..  ] the blocks, Uint16 little-endian
 *
 * Ten bytes of header before the payload keeps the Uint16 view two-byte
 * aligned, which is the same reason the save format pads.
 */
export const CHUNK_MAGIC = 0x43484e4b; // "CHNK"
/** Magic, three coordinates, and how the blocks are written. */
export const CHUNK_HEADER = 12;
/** Every block written out, two bytes each. */
export const CHUNK_RAW = 0;
/** Runs of the same block: value, count, value, count. */
export const CHUNK_RUNS = 1;

/**
 * A chunk as bytes, the shorter of two ways.
 *
 * Most chunks are air, and most of the rest are a few layers of the same
 * stone under the same earth: written as runs - a block and how many of it
 * in a row - a chunk of air is one run and a chunk of hillside is a few
 * hundred. Written out flat it is eight kilobytes every time, and a join
 * on the кремль is three thousand of them, so a quarter of the world was
 * going over the wire as zeros. A chunk with no runs to speak of - a wall
 * of alternating blocks - is written flat, because the runs would be
 * longer; the header says which.
 */
export function encodeChunk(
  cx: number, cy: number, cz: number, blocks: Uint16Array,
): ArrayBuffer {
  // Count first, so the buffer is made once at its final size.
  let runs = 0;
  for (let i = 0; i < blocks.length;) {
    const v = blocks[i]!;
    let j = i + 1;
    while (j < blocks.length && blocks[j] === v && j - i < 0xffff) j++;
    runs++;
    i = j;
  }
  const flat = blocks.length * 2;
  const packed = runs * 4;
  const format = packed < flat ? CHUNK_RUNS : CHUNK_RAW;
  const buffer = new ArrayBuffer(CHUNK_HEADER + (format === CHUNK_RUNS ? packed : flat));
  const view = new DataView(buffer);
  view.setUint32(0, CHUNK_MAGIC);
  view.setInt16(4, cx);
  view.setInt16(6, cy);
  view.setInt16(8, cz);
  view.setUint16(10, format);
  const body = new Uint16Array(buffer, CHUNK_HEADER);
  if (format === CHUNK_RAW) {
    body.set(blocks);
    return buffer;
  }
  let at = 0;
  for (let i = 0; i < blocks.length;) {
    const v = blocks[i]!;
    let j = i + 1;
    while (j < blocks.length && blocks[j] === v && j - i < 0xffff) j++;
    body[at++] = v;
    body[at++] = j - i;
    i = j;
  }
  return buffer;
}

export interface ChunkFrame {
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  readonly blocks: Uint16Array;
}

/**
 * Read a chunk back, or null for anything that is not one.
 *
 * Runs that do not add up to a whole chunk are refused rather than padded:
 * a frame cut short or padded out is a frame somebody else wrote.
 */
export function decodeChunk(buffer: ArrayBuffer, volume = 4096): ChunkFrame | null {
  if (buffer.byteLength < CHUNK_HEADER) return null;
  const view = new DataView(buffer);
  if (view.getUint32(0) !== CHUNK_MAGIC) return null;
  if ((buffer.byteLength - CHUNK_HEADER) % 2 !== 0) return null;
  const where = {
    cx: view.getInt16(4),
    cy: view.getInt16(6),
    cz: view.getInt16(8),
  };
  const format = view.getUint16(10);
  const body = new Uint16Array(buffer.slice(CHUNK_HEADER));
  if (format === CHUNK_RAW) {
    if (body.length !== volume) return null;
    return { ...where, blocks: body };
  }
  if (format !== CHUNK_RUNS || body.length % 2 !== 0) return null;
  const blocks = new Uint16Array(volume);
  let at = 0;
  for (let i = 0; i < body.length; i += 2) {
    const v = body[i]!;
    const n = body[i + 1]!;
    if (n === 0 || at + n > volume) return null;
    blocks.fill(v, at, at + n);
    at += n;
  }
  if (at !== volume) return null;
  return { ...where, blocks };
}

/**
 * How far a player may have moved since we last heard from them.
 *
 * Generous on purpose: the point is not to police the last centimetre, it is
 * to refuse the person who claims to be four hundred blocks away inside a
 * locked room. A jump, a fall and a lagged frame all have to fit under it, so
 * it is the fastest a body can travel times a wide margin.
 */
export const MAX_SPEED = 24;

/**
 * Is this the word that gets you in?
 *
 * Compared with the case and the spaces taken off, because the word is read
 * aloud or typed from a message and neither carries capitals reliably. Not
 * compared in constant time: this is a word a friend gives a friend for a
 * game of blocks, not a password, and pretending otherwise by borrowing the
 * machinery of one would be theatre.
 */
export function wordMatches(expected: string, given: string): boolean {
  return expected.trim().toLowerCase() === given.trim().toLowerCase();
}

/**
 * Is this somewhere they could have got to?
 *
 * @param dt seconds since their last accepted position.
 */
export function plausibleMove(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
  dt: number,
): boolean {
  if (!Number.isFinite(to.x) || !Number.isFinite(to.y) || !Number.isFinite(to.z)) {
    return false;
  }
  // A lag spike must not teleport somebody across the map when it clears, so
  // the window a single message may cover is capped.
  const window = Math.min(Math.max(dt, 0), 1);
  const allowed = MAX_SPEED * window + 1;
  return Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z) <= allowed;
}
